import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchProximity, saveProximity, clearProximity } from "@/pages/beta/courseFitApi";
import { REL, MODE, IMP, FREQ, HOURS, blank, anchorLabel, describeAnchor } from "@/lib/proximity";

const pill = (on) => `inline-flex min-h-[44px] cursor-pointer items-center rounded-full border px-4 py-2 text-sm ${on ? "border-[#7C3AED] bg-[#F5F0FF] text-[#4C1D95]" : "border-stone-300 text-stone-700"} focus-within:ring-2 focus-within:ring-[#7C3AED]`;

function Choice({ name, value, options, onChange, multi, testId }) {
  return (
    <div className="flex flex-wrap gap-2" role={multi ? "group" : "radiogroup"} data-testid={testId}>
      {Object.entries(options).map(([v, l]) => {
        const on = multi ? value.includes(v) : value === v;
        return (
          <label key={v} className={pill(on)}>
            <input type={multi ? "checkbox" : "radio"} name={name} className="sr-only" checked={on} data-testid={`${testId}-${v}`}
              onChange={() => onChange(multi ? (on ? value.filter((x) => x !== v) : [...value, v]) : v)} />
            {l}
          </label>
        );
      })}
    </div>
  );
}

function AnchorFields({ a, i, set }) {
  const custom = !HOURS.includes(a.max_hours);
  const up = (k, v) => set({ ...a, [k]: v });
  return (
    <fieldset className="space-y-4 rounded-2xl border border-stone-200 bg-white p-4" data-testid={`prox-anchor-${i}`}>
      <legend className="sr-only">{i === 0 ? "Who or where?" : "A second place"}</legend>
      <div aria-hidden="true" className="text-sm font-medium text-stone-800" data-testid={`prox-anchor-title-${i}`}>{i === 0 ? "Who or where?" : "A second place"}</div>
      <Choice name={`rel-${i}`} value={a.relationship} options={REL} onChange={(v) => up("relationship", v)} testId={`prox-rel-${i}`} />
      <label className="block text-sm text-stone-700">Town, city or postcode
        <input value={a.location} onChange={(e) => up("location", e.target.value)} maxLength={120} data-testid={`prox-location-${i}`}
          className="mt-1 block w-full min-h-[44px] rounded-lg border border-stone-300 px-3 text-base" placeholder="e.g. Leeds or LS6" />
      </label>
      <div>
        <div className="text-sm text-stone-700">Longest journey you'd be happy with</div>
        <div className="mt-2 flex flex-wrap gap-2" role="radiogroup" data-testid={`prox-hours-${i}`}>
          {HOURS.map((h) => (
            <label key={h} className={pill(a.max_hours === h)}>
              <input type="radio" name={`hrs-${i}`} className="sr-only" checked={a.max_hours === h} onChange={() => up("max_hours", h)} data-testid={`prox-hours-${i}-${h}`} />
              {h} hr{h > 1 ? "s" : ""}
            </label>
          ))}
          <label className={pill(custom)}>
            <input type="radio" name={`hrs-${i}`} className="sr-only" checked={custom} onChange={() => up("max_hours", 5)} data-testid={`prox-hours-${i}-custom`} />
            Custom
          </label>
        </div>
        {custom && (
          <label className="mt-2 block text-sm text-stone-700">Hours
            <input type="number" min="0.5" max="24" step="0.5" value={a.max_hours} onChange={(e) => up("max_hours", Number(e.target.value) || 0)}
              className="ml-2 w-24 min-h-[44px] rounded-lg border border-stone-300 px-3" data-testid={`prox-hours-${i}-value`} />
          </label>
        )}
      </div>
      <div>
        <div className="text-sm text-stone-700">How would you travel?</div>
        <div className="mt-2"><Choice name={`mode-${i}`} value={a.modes} options={MODE} onChange={(v) => up("modes", v)} multi testId={`prox-mode-${i}`} /></div>
        {a.modes.includes("flights") && <p className="mt-1 text-xs text-stone-500">With flights, count the whole door-to-door journey, including getting to and through the airport.</p>}
      </div>
      <label className="flex min-h-[44px] items-center gap-2 text-sm text-stone-700">
        <input type="checkbox" checked={a.international} onChange={(e) => set({ ...a, international: e.target.checked, visit_frequency: e.target.checked ? a.visit_frequency : null })} data-testid={`prox-intl-${i}`} className="h-5 w-5" />
        This place is in a different country from where I'd study
      </label>
      {a.international && (
        <label className="block text-sm text-stone-700">How often would you expect to visit?
          <select value={a.visit_frequency || ""} onChange={(e) => up("visit_frequency", e.target.value || null)} data-testid={`prox-freq-${i}`}
            className="mt-1 block min-h-[44px] w-full rounded-lg border border-stone-300 bg-white px-3">
            <option value="">Not sure yet</option>
            {Object.entries(FREQ).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
      )}
      <div>
        <div className="text-sm text-stone-700">How important is this?</div>
        <div className="mt-2"><Choice name={`imp-${i}`} value={a.importance} options={IMP} onChange={(v) => up("importance", v)} testId={`prox-imp-${i}`} /></div>
      </div>
    </fieldset>
  );
}

export function ProximityPreferenceForm({ signedIn, onAnchors }) {
  const [wants, setWants] = useState(null);
  const [anchors, setAnchors] = useState([blank()]);
  const [saved, setSaved] = useState(null);
  const [status, setStatus] = useState(null);
  const [editing, setEditing] = useState(false);
  useEffect(() => {
    if (!signedIn) return;
    fetchProximity().then((r) => {
      const p = r.ok && r.data.preference;
      if (!p) return;
      setSaved(p); setWants(p.wants);
      if (p.anchors?.length) setAnchors(p.anchors);
    });
  }, [signedIn]);
  useEffect(() => { onAnchors?.(saved?.wants ? saved.anchors.map((a) => ({ label: anchorLabel(a) })) : []); }, [saved, onAnchors]);
  const valid = anchors.every((a) => a.location.trim().length >= 2 && a.modes.length && a.max_hours > 0);
  const save = async () => {
    setStatus("saving");
    const r = await saveProximity({ wants: !!wants, anchors: wants ? anchors : [] });
    if (r.ok) { setSaved(r.data.preference); setStatus("saved"); setEditing(false); } else setStatus("error");
  };
  const clear = async () => {
    const r = await clearProximity();
    if (r.ok) { setSaved(null); setWants(null); setAnchors([blank()]); setStatus("cleared"); setEditing(false); }
  };
  if (saved && !editing) {
    return (
      <section id="prox" className="mt-10 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-stone-200 bg-[#F7F3EC] px-5 py-4 print:hidden" data-testid="prox-collapsed">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-[0.14em] text-[#7C3AED]">Travel preference · saved</div>
          <p className="mt-1 text-sm text-stone-800" data-testid="prox-collapsed-summary">
            {saved.wants ? saved.anchors.map(describeAnchor).join("; ") : "Staying close to anyone or anywhere isn't a factor for you."}
          </p>
          {status === "saved" && <p className="mt-1 text-xs text-stone-600" data-testid="prox-status">Saved. It's shown in My Realisations.</p>}
        </div>
        <button type="button" onClick={() => { setEditing(true); setStatus(null); }} data-testid="prox-edit"
          className="min-h-[40px] rounded-full border border-stone-300 bg-white px-4 text-sm text-stone-800 transition-colors hover:border-stone-500">Change</button>
      </section>
    );
  }
  return (
    <section id="prox" className="mt-10 rounded-3xl bg-[#F7F3EC] p-5 sm:p-8 print:hidden" data-testid="prox-form">
      <div className="text-[11px] uppercase tracking-[0.14em] text-[#7C3AED]">Optional · practical preference</div>
      <h2 className="mt-1 font-serif text-2xl text-stone-900">Would you like to stay within easy reach of someone or somewhere?</h2>
      <div className="mt-4 flex flex-wrap gap-2" role="radiogroup" data-testid="prox-wants">
        {[[true, "Yes"], [false, "No, that's not a factor"]].map(([v, l]) => (
          <label key={l} className={pill(wants === v)}>
            <input type="radio" name="prox-wants" className="sr-only" checked={wants === v} onChange={() => { setWants(v); setStatus(null); }} data-testid={`prox-wants-${v ? "yes" : "no"}`} />
            {l}
          </label>
        ))}
      </div>
      {wants && (
        <div className="mt-5 space-y-4">
          {anchors.map((a, i) => (
            <AnchorFields key={i} a={a} i={i} set={(n) => setAnchors(anchors.map((x, j) => (j === i ? n : x)))} />
          ))}
          <div className="flex flex-wrap gap-2">
            {anchors.length < 2 && <button type="button" onClick={() => setAnchors([...anchors, blank()])} className={pill(false)} data-testid="prox-add-anchor">+ Add a second place</button>}
            {anchors.length > 1 && <button type="button" onClick={() => setAnchors(anchors.slice(0, 1))} className={pill(false)} data-testid="prox-remove-anchor">Remove the second place</button>}
          </div>
        </div>
      )}
      <p className="mt-5 text-sm text-stone-600" data-testid="prox-honesty">
        WUA doesn't have verified journey times yet. So nothing is hidden or filtered by travel time, even if you mark it Essential.
        Where it matters, you'll see "journey time not verified". The places you enter stay inside WUA; they aren't sent to a maps service.
      </p>
      {signedIn ? (
        <div className="mt-4 flex flex-wrap items-center gap-3">
          {wants !== null && (
            <button type="button" disabled={wants && !valid} onClick={save} data-testid="prox-save"
              className="min-h-[44px] rounded-full bg-stone-900 px-5 text-sm font-medium text-white disabled:opacity-40">Save preference</button>
          )}
          {saved && <button type="button" onClick={clear} className="min-h-[44px] text-sm text-stone-600 underline" data-testid="prox-clear">Remove saved preference</button>}
          {saved && <button type="button" onClick={() => { setEditing(false); setWants(saved.wants); setAnchors(saved.anchors?.length ? saved.anchors : [blank()]); }} className="min-h-[44px] text-sm text-stone-600 underline" data-testid="prox-cancel-edit">Cancel</button>}
          <span aria-live="polite" className="text-sm text-stone-600" data-testid="prox-status">
            {status === "saving" ? "Saving…" : status === "saved" ? "Saved. It's shown in My Realisations." : status === "error" ? "Couldn't save. Check the place names and try again." : status === "cleared" ? "Removed." : ""}
          </span>
        </div>
      ) : (
        <p className="mt-4 text-sm text-stone-700" data-testid="prox-signin-note">Sign in to save this preference. It isn't stored while you're signed out.</p>
      )}
    </section>
  );
}

export function ProximitySummary() {
  const [p, setP] = useState(undefined);
  useEffect(() => { fetchProximity().then((r) => setP(r.ok ? r.data.preference : null)); }, []);
  if (!p) return null;
  return (
    <section className="rounded-2xl border border-stone-200 bg-white p-5" data-testid="prox-summary">
      <div className="text-[11px] uppercase tracking-[0.14em] text-[#7C3AED]">What you've told us · travel</div>
      {p.wants ? (
        <>
          <ul className="mt-2 space-y-1 text-[15px] text-stone-800">
            {p.anchors.map((a, i) => <li key={i} data-testid={`prox-summary-anchor-${i}`}>{describeAnchor(a)}</li>)}
          </ul>
          <p className="mt-2 text-sm text-stone-600">
            You said this yourself; WUA hasn't inferred it. Journey times aren't verified yet, so no course or city has been filtered out because of it.
          </p>
        </>
      ) : (
        <p className="mt-2 text-[15px] text-stone-800" data-testid="prox-summary-none">You told WUA that staying close to anyone or anywhere isn't a factor for you.</p>
      )}
      <Link to="/beta/course-fit" className="mt-2 inline-flex min-h-[44px] items-center text-sm text-[#7C3AED] underline" data-testid="prox-summary-change">Change this in Fast Match</Link>
    </section>
  );
}
